import { forwardRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useTutorialStore } from '../store/tutorialStore';
import { useFleetStore } from '../store/fleetStore';
import { usePlanetResource } from '../hooks/usePlanetResource';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { formatCompactNumber, formatFullNumber } from '../utils/formatters';
import {
    calculateIncomingStationPopulation,
    calculateTeamPlanetUsedPopulation,
    calculateUsedPopulation,
    isPlanetOverpopulated,
    isPlanetOzoneLocked,
} from '@kaeliux/game-engine';
import { BUILDING_NAMES, RESOURCE_NAMES, getTutorialSteps, type BuildingType, type BuildingZone } from '@kaeliux/shared';
import { useMyAlliance } from '../hooks/useMyAlliance';
import { useTeamPlanet, useTeamPlanetFlights } from '../hooks/useTeamPlanet';
import { TEAM_PLANET_HOME_ROUTE } from '../hooks/useTeamPlanetView';
import { zoneRouteFrom } from '../contexts/TeamPlanetViewContext';
import { isTeamPlanetRoute } from '../utils/teamPlanetStyle';
import { Icon, type IconName } from './ui/Icon';
import { Chip } from './ui/Chip';
import { StatTile, type StatTileAccent, type StatTileStatus } from './ui/StatTile';
import { Badge, type BadgeTone } from './ui/Badge';
import { IconButton } from './ui/IconButton';
import { Spinner } from './ui/Spinner';
import { cn } from '../utils/cn';
import PlanetSwitcher from './PlanetSwitcher';
import { RACE_PORTRAITS } from '../utils/raceAssets';

function planetImage(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

/** Preserved from `TopResourceBar`: >95% danger, >85% warning, >75% success, else normal. */
export function getWarningLevel(current: number, capacity: number): StatTileStatus {
    if (capacity <= 0) return 'normal';
    const ratio = (current / capacity) * 100;
    if (ratio > 95) return 'danger';
    if (ratio > 85) return 'warning';
    return 'normal';
}

/**
 * Where each tile sends the player: the building that governs that resource,
 * highlighted in its zone. Same `?highlight=` convention the requirement
 * grids and the page-level redirects already use.
 */
type ResourceTileId = 'metal' | 'minerals' | 'gas' | 'energy' | 'population' | 'scrap';

const RESOURCE_TILE_LABELS: Record<ResourceTileId, string> = {
    metal: RESOURCE_NAMES.metal,
    minerals: RESOURCE_NAMES.minerals,
    gas: RESOURCE_NAMES.gas,
    energy: RESOURCE_NAMES.energy,
    population: 'Population',
    scrap: RESOURCE_NAMES.scrap,
};

/** Same cadence as the Team planet screens poll the planet itself (`TeamPlanetLayout`). */
const TEAM_PLANET_FLIGHTS_POLL_MS = 15_000;

const RESOURCE_BUILDINGS: Record<ResourceTileId, [BuildingZone, BuildingType]> = {
    metal: ['resource', 'metalMine'],
    minerals: ['resource', 'mineralRefinery'],
    gas: ['resource', 'gasPlant'],
    energy: ['resource', 'solarPlant'],
    population: ['resource', 'hangars'],
    scrap: ['industry', 'recyclingPlant'],
};

interface ResourceTileSpec {
    id: ResourceTileId;
    route: string;
    icon: IconName;
    accent: StatTileAccent;
    current: number;
    capacity: number | null;
    sublabel: string;
    /**
     * Issue #55: the stockpiled resources compact to "23.9 M"; Energy and
     * Population keep their full numbers. The exact value is on the hover
     * title either way.
     */
    compact: boolean;
    /** Population on its way to the planet (incoming Station flights), shown as "(+N)" until it docks. */
    incoming?: number;
}

/** The per-hour sublabel the three stockpiled resources carry. */
function rateSublabel(rate: number) {
    return `+${formatCompactNumber(rate)} / hr`;
}

function ResourceTile({ id, route, icon, accent, current, capacity, sublabel, compact, incoming = 0 }: ResourceTileSpec) {
    const navigate = useNavigate();
    const label = RESOURCE_TILE_LABELS[id];
    const status = capacity ? getWarningLevel(current, capacity) : 'normal';
    const format = compact ? formatCompactNumber : formatFullNumber;
    // The " / capacity" suffix roughly doubles a tile's width, which is what
    // pushed the third tile off a phone-width bar. It is hidden below lg; the
    // full "current / capacity" reading stays in the tile's title tooltip.
    const value = capacity ? (
        <>
            {format(current)}
            <span className="hidden lg:inline">{` / ${format(capacity)}`}</span>
        </>
    ) : (
        format(current)
    );

    return (
        <button
            type="button"
            onClick={() => navigate(route)}
            aria-label={`${label}: go to its building`}
            className="flex flex-col items-start gap-0.5 shrink-0 rounded-chip text-left hover:brightness-125 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--zone-accent)]"
            data-testid={`hud-resource-${id}`}
        >
            <span className="text-micro text-content-muted pl-0.5">{label}</span>
            <StatTile
                icon={<Icon name={icon} size={17} />}
                value={
                    <>
                        <span data-testid={`hud-value-${id}`}>{value}</span>
                        {incoming > 0 && (
                            <span data-testid={`hud-incoming-${id}`} className="ml-1 text-status-info">
                                {`(+${format(incoming)})`}
                            </span>
                        )}
                    </>
                }
                sublabel={sublabel}
                accent={accent}
                status={status}
                title={`${label}: ${formatFullNumber(current)}${capacity ? ` / ${formatFullNumber(capacity)}` : ''}${incoming > 0 ? ` (+${formatFullNumber(incoming)} incoming)` : ''}`}
            />
        </button>
    );
}

/**
 * Single translucent glass HUD replacing the old opaque `AppShell` nav plus
 * the per-page `TopResourceBar`. `fixed`, so callers must pad their content
 * area by this component's measured height (see the `ref` this forwards).
 */
export const GameHUD = forwardRef<HTMLElement>(function GameHUD(_props, ref) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { profile, session, logout } = useAuthStore();
    const personalPlanet = usePlanetStore((s) => s.currentPlanet);
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);
    const tradeTokens = usePlayerStore((s) => s.premium?.tradeTokens ?? 0);
    const { state: { isTicking: isTickingPersonal } } = usePlanetResource();
    const { resourceMultiplier } = useUniverseStore();
    const { openDialog } = useConfirmDialog();

    // On `/team-planet/*` the HUD describes the alliance's Team planet, read
    // from the same cache entry the Team planet screens poll.
    const onTeamPlanetRoute = isTeamPlanetRoute(pathname);
    const allianceQuery = useMyAlliance({ enabled: onTeamPlanetRoute });
    const teamPlanetQuery = useTeamPlanet({ enabled: onTeamPlanetRoute && !!allianceQuery.data });
    // Every member's Stations heading to the Team planet, not only the
    // viewer's own — polled like the Team planet screens themselves.
    const teamPlanetFlightsQuery = useTeamPlanetFlights({
        enabled: onTeamPlanetRoute && !!teamPlanetQuery.data,
        refetchInterval: TEAM_PLANET_FLIGHTS_POLL_MS,
    });
    const teamPlanet = onTeamPlanetRoute ? teamPlanetQuery.data ?? null : null;
    const allianceTag = allianceQuery.data?.alliance.tag;
    // On a Team planet route the HUD never falls back to the personal planet:
    // while the Team planet loads (or is missing) it shows no planet at all.
    const planet = onTeamPlanetRoute ? teamPlanet : personalPlanet;
    const isTicking = teamPlanet ? teamPlanetQuery.isFetching : isTickingPersonal;
    const zoneRoute = zoneRouteFrom(teamPlanet ? TEAM_PLANET_HOME_ROUTE : '');
    const resourceRoute = (id: ResourceTileId) => zoneRoute(...RESOURCE_BUILDINGS[id]);

    const tutorialActive = useTutorialStore((s) => s.isActive);
    const tutorialStepIndex = useTutorialStore((s) => s.stepIndex);
    const tutorialDisabled = useTutorialStore((s) => s.disabled);
    const tutorialStepReady = useTutorialStore((s) => s.stepReady);
    const openTutorial = useTutorialStore((s) => s.open);
    const tutorialHasMoreToDo = tutorialStepIndex < getTutorialSteps(profile?.race ?? 'humans').length - 1;
    const tutorialShouldPulse = !tutorialDisabled && !tutorialActive && tutorialStepReady && tutorialHasMoreToDo;
    const portrait = profile?.race ? RACE_PORTRAITS[profile.race] : undefined;

    const usedPopulation = planet
        ? teamPlanet
            ? calculateTeamPlanetUsedPopulation(planet.teamShips, planet.tickCheckpoints ?? [])
            : profile?.race
                ? calculateUsedPopulation(profile.race, planet.ships, planet.tickCheckpoints, planet.shipsInFlight)
                : 0
        : 0;
    const fleets = useFleetStore((s) => s.view?.fleets);
    // On a Team planet the coordinator counts every member's Stations; until
    // it has answered (or if it fails), the viewer's own are still known.
    const ownIncomingPopulation = planet && profile?.race
        ? calculateIncomingStationPopulation(profile.race, fleets, planet.coordinates)
        : 0;
    const incomingPopulation = teamPlanet
        ? teamPlanetFlightsQuery.data?.incomingStationPopulation ?? ownIncomingPopulation
        : ownIncomingPopulation;

    const ozoneLocked = planet && !teamPlanet ? isPlanetOzoneLocked(planet) : false;
    const overpopulated = planet && !teamPlanet && profile?.race ? isPlanetOverpopulated(profile.race, planet) : false;
    const statusBadge: { label: string; tone: BadgeTone } = teamPlanet
        ? { label: 'Shared', tone: 'info' }
        : ozoneLocked
            ? { label: 'Drought', tone: 'danger' }
            : overpopulated
                ? { label: 'Overpopulated', tone: 'warning' }
                : { label: 'Stable', tone: 'success' };
    const subtitle = teamPlanet
        ? `Team Planet${allianceTag ? ` [${allianceTag}]` : ''}`
        : profile?.race ? `${profile.race} Colony` : 'Colony';

    const version = import.meta.env.VITE_APP_VERSION as string | undefined;

    const effectiveRate = (rate: number) => Math.floor(rate * resourceMultiplier);

    const tiles: ResourceTileSpec[] | null = planet
        ? [
            {
                id: 'metal',
                route: resourceRoute('metal'),
                icon: 'metal',
                accent: 'metal',
                current: planet.resources.metal.current,
                capacity: planet.resources.metal.capacity,
                sublabel: rateSublabel(effectiveRate(planet.resources.metal.rate)),
                compact: true,
            },
            {
                id: 'minerals',
                route: resourceRoute('minerals'),
                icon: 'mineral',
                accent: 'mineral',
                current: planet.resources.minerals.current,
                capacity: planet.resources.minerals.capacity,
                sublabel: rateSublabel(effectiveRate(planet.resources.minerals.rate)),
                compact: true,
            },
            {
                id: 'gas',
                route: resourceRoute('gas'),
                icon: 'gas',
                accent: 'gas',
                current: planet.resources.gas.current,
                capacity: planet.resources.gas.capacity,
                sublabel: rateSublabel(effectiveRate(planet.resources.gas.rate)),
                compact: true,
            },
            {
                id: 'energy',
                route: resourceRoute('energy'),
                icon: 'energy',
                accent: 'energy',
                current: planet.resources.energy.rate - (planet.resources.energy.consumed ?? 0),
                capacity: null,
                sublabel: 'net',
                compact: false,
            },
            {
                id: 'population',
                route: resourceRoute('population'),
                icon: 'population',
                accent: 'population',
                current: usedPopulation,
                capacity: planet.resources.population.capacity,
                sublabel: 'used',
                compact: false,
                incoming: incomingPopulation,
            },
            {
                id: 'scrap',
                route: resourceRoute('scrap'),
                icon: 'scrap',
                accent: 'resource',
                current: planet.resources.scrap.current,
                capacity: null,
                sublabel: 'stockpile',
                compact: true,
            },
        ]
        : null;

    // Six resource tiles need ~635px; a phone HUD row has ~350px. Below lg only
    // the three stockpiled resources are shown and the rest are one tap away,
    // rather than hiding them behind a sideways scroll.
    const [showAllResources, setShowAllResources] = useState(false);

    const confirmLogout = () => openDialog({
        title: 'Log out?',
        message: "You'll need to sign in again to access your account.",
        warning: session?.isGuest
            ? "You're playing as a guest. The only way back into this game is your guest ID, shown on the Personal page. Save it first, or create an account to keep your progress for good."
            : undefined,
        confirmLabel: 'Log out',
        danger: true,
        onConfirm: logout,
    });

    return (
        <nav
            ref={ref}
            className="fixed top-0 inset-x-0 z-50 flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-2.5 bg-surface-hud backdrop-blur-hud border-b border-hairline lg:flex-nowrap lg:justify-between"
        >
            {/* Below lg the bar stacks: identity + session controls, then the
                resource strip, then the currency chips. Each group is a direct
                child of the wrapping nav so it can claim its own row; the
                `lg:order-*` classes put them back in the original single-row
                order from lg up, where the nav stops wrapping. */}
            <div className="flex w-full min-w-0 items-center justify-between gap-3 lg:contents">
                <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden lg:order-1 lg:flex-none lg:shrink" title={version ? `v${version}` : undefined}>
                    {planet && (
                        <img
                            src={planetImage(planet.planetType)}
                            alt=""
                            className="h-[42px] w-[42px] rounded-full object-cover border border-hairline-strong bg-space-950 shrink-0"
                        />
                    )}
                    <div className="flex flex-col leading-tight min-w-0">
                        <span className="text-title text-content-primary truncate">
                            {planet?.name ?? profile?.displayName ?? 'Kaeliux'}
                        </span>
                        {planet && (
                            <span className="flex items-center gap-1.5 text-label text-content-muted min-w-0 overflow-hidden whitespace-nowrap">
                                <PlanetSwitcher />
                                <span aria-hidden="true">·</span>
                                <span className="truncate" data-testid="hud-planet-subtitle">{subtitle}</span>
                                <Badge tone={statusBadge.tone}>{statusBadge.label}</Badge>
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 lg:order-4">
                    {portrait && (
                        <div className="relative shrink-0">
                            {tutorialShouldPulse && (
                                <div className="absolute top-full right-0 mt-3 whitespace-nowrap rounded-control bg-space-700 border border-space-500 px-3 py-1.5 text-xs text-white shadow-lg pointer-events-none">
                                    Commander, I have a message for you.
                                    <span className="absolute bottom-full right-5 border-4 border-transparent border-b-space-500" />
                                </div>
                            )}
                            <button
                                onClick={openTutorial}
                                aria-label="Tutorial"
                                title="Tutorial"
                                className="relative w-12 h-12 shrink-0 rounded-full"
                            >
                                <span
                                    key={tutorialStepIndex}
                                    aria-hidden="true"
                                    className={`absolute -inset-1 rounded-full pointer-events-none ${tutorialShouldPulse ? 'animate-highlight-blink' : ''}`}
                                />
                                <img
                                    src={portrait}
                                    alt="Tutorial"
                                    className={`w-12 h-12 rounded-full object-cover object-top border-2 border-space-600 transition-[filter] duration-300 ${tutorialActive ? 'saturate-100' : tutorialDisabled ? 'grayscale' : 'saturate-50'}`}
                                />
                            </button>
                        </div>
                    )}

                    <IconButton variant="ghost" aria-label="Logout" onClick={confirmLogout}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 100 100">
                            <g fill="none" stroke="#ea4a4a" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round">
                                <path d="m 50,10 0,35" />
                                <path d="M 26,20 C -3,48 16,90 51,90 79,90 89,67 89,52 89,37 81,26 74,20" />
                            </g>
                        </svg>
                    </IconButton>
                </div>
            </div>

                {/* The toggle sits outside the scroller: inside it, it scrolled off
                the right edge and the extra resources became unreachable. */}
            <div className="flex w-full basis-full min-w-0 items-center gap-2 lg:contents">
            <div className="min-w-0 flex-1 overflow-x-auto lg:order-2 lg:basis-0">
                    {tiles && (
                        <div
                            className={cn(
                                'flex items-center gap-2 lg:w-max lg:flex-nowrap lg:mx-auto',
                                // Expanded, the extra tiles wrap onto a new row: left in
                                // one row they sat off the right edge, so the tap
                                // revealed nothing without a sideways scroll.
                                showAllResources ? 'flex-wrap' : 'w-max',
                            )}
                        >
                            <div className="flex items-center gap-2" data-tutorial-id="resource-bar-left">
                                {tiles.slice(0, 3).map((tile) => (
                                    <ResourceTile key={tile.id} {...tile} />
                                ))}
                            </div>
                            <div
                                className={cn('items-center gap-2 lg:flex', showAllResources ? 'flex' : 'hidden')}
                                data-tutorial-id="resource-bar-right"
                            >
                                {tiles.slice(3).map((tile) => (
                                    <ResourceTile key={tile.id} {...tile} />
                                ))}
                            </div>
                            {isTicking && (
                                <Spinner
                                    className="text-content-muted shrink-0"
                                    size="sm"
                                    decorative={false}
                                    aria-label="Refreshing resources"
                                />
                            )}
                        </div>
                    )}
                </div>

                <IconButton
                    variant="ghost"
                    className="shrink-0 lg:hidden"
                    data-testid="toggle-resources"
                    aria-expanded={showAllResources}
                    aria-label={showAllResources ? `Hide ${RESOURCE_NAMES.energy}, population and ${RESOURCE_NAMES.scrap}` : `Show ${RESOURCE_NAMES.energy}, population and ${RESOURCE_NAMES.scrap}`}
                    onClick={() => setShowAllResources((open) => !open)}
                >
                    <Icon name={showAllResources ? 'minus' : 'plus'} size={16} />
                </IconButton>
            </div>

                <div className="flex w-full basis-full items-center justify-end gap-2 lg:order-3 lg:w-auto lg:basis-auto lg:shrink-0">
                    <Chip
                        tone="warning"
                        leadingIcon={<Icon name="dark-matter" size={16} />}
                        data-testid="dark-matter-nav"
                        title="Premium Store"
                        onClick={() => navigate('/premium')}
                    >
                        {formatFullNumber(darkMatter)}
                    </Chip>
                    <Chip
                        tone="info"
                        leadingIcon={<Icon name="trade-token" size={16} />}
                        data-testid="trade-tokens-nav"
                        title={BUILDING_NAMES.tradeCenter}
                        onClick={() => navigate('/trade-center')}
                    >
                        {formatFullNumber(tradeTokens)}
                    </Chip>
                </div>
        </nav>
    );
});

export default GameHUD;
